import { IFeaturesConfig } from './baseTypes';
import { Mango } from './feedTypes';
import { IUserScore } from './gamificationTypes';
import { ITQProfileCompletionResponse } from './tqConnectTypes/user';

export interface IUserDetails {
  userSlug: string;
  mangoes: string[];
  showtwitter: boolean;
  showfacebook: boolean;
  showinstagram: boolean;
  showyoutube: boolean;
  showlinkedin: boolean;
  isDeleted: boolean;
  _id: string;
  email_verified: boolean;
  phone: number;
  onboarding: string;
  expireIn: Date;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  email?: string;
  name: string;
  profilePicUrl?: string;
  videoUrl: string;
  videoUrlUncompressed: string;
  accountNumber?: string;
  about?: string;
  styledAbout?: string;
  accountName?: string;
  birthday?: string;
  gender?: string;
  ifsc?: string;
  bankName?: string;
  otp: number;
  showInbox?: boolean;
  creatorAccessRequested?: ICreatorApproveStatus;
  registeredBy: 'email' | 'phone';
  country?: string;
  isDeactivated?: boolean;
  deactivationDate?: string;
  deleteRequestDate?: string;
  shortUrl?: string;
  gstNumber?: string;
  gstRegistrationAddress?: string;
  gstRegistrationName?: string;
  gstState?: string;
  pan?: string;
  panImage?: string;
  disabledChat?: boolean;
  currency?: string;
  tmCommission?: number;
  internationalBankAccount?: {
    account_number: string;
    bank_name: string;
    account_holder_name: string;
    routing_number: string;
  };
  isEmailVerified?: boolean;
  requiresPostMigrationVerification?: boolean;
  isBlockedFromCommunityEngagement?: boolean;
  score?: IUserScore;
  host?: string;
  whitelabelPlanType?: WhitelabelPlan;
  userTier?: CreatorTierType;
  accountSettingTierSubscriptionExpiry?: string;
  // Razorpay autopay status of the tier subscription ("active" | "authenticated"
  // ⇒ it will auto-renew, so no pre-expiry notice).
  accountSettingTierRazorpaySubscriptionIsAutopayStatus?: string | null;
  // A tier plan already bought and queued to start later (TMG-3904): when it
  // starts, and which tier it is (named in the notice once the backend sends it).
  scheduledPlanActivateAt?: string | null;
  scheduledPlanTier?: string | null;
  dialCode?: string;
  userAccessPermissions?: Array<UAMPermissions>;
  featuresConfig?: IFeaturesConfig;

  // for TQ Connect
  tqConnectScore?: number;
  tqConnectProfileCompletionResponses?: ITQProfileCompletionResponse[];
  tqConnectBadge?: string;
  tqScoreLastUpdatedAt?: string; // date the score was last updated (ISO string)
  tqReviewReceived?: number; // number of reviews received = number of service requests completed
}

interface ICreatorCategoryBase {
  _id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface ICreatorCategory extends ICreatorCategoryBase {
  children: ICreatorCategoryChild[];
}

interface ICreatorCategoryChild extends ICreatorCategoryBase {
  parent: string;
}

export interface SelectedItemMap {
  [key: string]: { selected: boolean; item: ICreatorCategoryBase };
}

export type ICreatorApproveStatus =
  | 'approved'
  | 'requested'
  | 'notrequested'
  | null;

export enum UAMPermissions {
  // Feed Permissions
  FEED_READ = 'FEED_READ',
  FEED_WRITE = 'FEED_WRITE',

  // Schedule Permissions
  SCHEDULER_READ = 'SCHEDULER_READ',
  SCHEDULER_WRITE = 'SCHEDULER_WRITE',

  // Course Permissions
  COURSE_ACCESS = 'COURSE_ACCESS',

  // Dashboard Permissions
  DASHBOARD_ACCESS = 'DASHBOARD_ACCESS',
  DASHBOARD_WORKSHOP_ACCESS = 'DASHBOARD_WORKSHOP_ACCESS',
  DASHBOARD_OVERVIEW_ACCESS = 'DASHBOARD_OVERVIEW_ACCESS',
  DASHBOARD_EARNINGS_READ = 'DASHBOARD_EARNINGS_READ',
  // DASHBOARD_EARNINGS_WRITE = 'DASHBOARD_EARNINGS_WRITE',
  DASHBOARD_TRANSACTIONS_READ = 'DASHBOARD_TRANSACTIONS_READ',
  DASHBOARD_SUBSCRIBERS_READ = 'DASHBOARD_SUBSCRIBERS_READ',
  DASHBOARD_CAMPAIGNS_ACCESS = 'DASHBOARD_CAMPAIGNS_ACCESS',
  DASHBOARD_UAM_ACCESS = 'DASHBOARD_UAM_ACCESS',
  DASHBOARD_ANALYTICS_ACCESS = 'DASHBOARD_ANALYTICS_ACCESS',
  DASHBOARD_PLATFORM_INTEGRATIONS_WRITE = 'DASHBOARD_PLATFORM_INTEGRATIONS_WRITE',
  DASHBOARD_EMAIL_PERSONALISATION_ACCESS = 'DASHBOARD_EMAIL_PERSONALISATION_ACCESS',
  DASHBOARD_CERTIFICATE_WRITE = 'DASHBOARD_CERTIFICATE_WRITE',
  DASHBOARD_ACCOUNTSETTINGS_ACCESS = 'DASHBOARD_ACCOUNTSETTINGS_ACCESS',
  DASHBOARD_PLATFORMSETTINGS_ACCESS = 'DASHBOARD_PLATFORMSETTINGS_ACCESS',
  DASHBOARD_AFFILIATE_ACCESS = 'DASHBOARD_AFFILIATE_ACCESS',
  DASHBOARD_STOREFRONT_ACCESS = 'DASHBOARD_STOREFRONT_ACCESS',
  DASHBOARD_CONSULTATION_ACCESS = 'DASHBOARD_CONSULTATION_ACCESS',
  DASHBOARD_GAMIFICATION_ACCESS = 'DASHBOARD_GAMIFICATION_ACCESS',
  DASHBOARD_TAX_REPORTS_ACCESS = 'DASHBOARD_TAX_REPORTS_ACCESS',
  DASHBOARD_MANGO_OVERVIEW_ACCESS = 'DASHBOARD_MANGO_OVERVIEW_ACCESS',
  DASHBOARD_MANGO_WEBSITES_ACCESS = 'DASHBOARD_MANGO_WEBSITES_ACCESS',
  DASHBOARD_LANDING_PAGE = 'DASHBOARD_LANDING_PAGE',
  DASHBOARD_COUPON_CODES_ACCESS = 'DASHBOARD_COUPON_CODES_ACCESS',
  DASHBOARD_LEADS_MANAGEMENT_READ = 'DASHBOARD_LEADS_MANAGEMENT_READ',
  DASHBOARD_WHATSAPP_TEMPLATES = 'DASHBOARD_WHATSAPP_TEMPLATES',
  DASHBOARD_WHATSAPP_EVENT_MAPPING = 'DASHBOARD_WHATSAPP_EVENT_MAPPING',
  DASHBOARD_WHATSAPP_CREDITS = 'DASHBOARD_WHATSAPP_CREDITS',
  DASHBOARD_TAKE_DOWN_REQUESTS_ACCESS = 'DASHBOARD_TAKE_DOWN_REQUESTS_ACCESS',
  DASHBOARD_RESTRICT_LOGIN_DEVICES_WRITE = 'DASHBOARD_RESTRICT_LOGIN_DEVICES_WRITE',
  DASHBOARD_UNSUBSCRIBED_USERS_ACCESS = 'DASHBOARD_UNSUBSCRIBED_USERS_ACCESS',
  DASHBOARD_REPORTED_CONTENT_ACCESS = 'DASHBOARD_REPORTED_CONTENT_ACCESS',
  DASHBOARD_BLOCKED_USERS_ACCESS = 'DASHBOARD_BLOCKED_USERS_ACCESS',
  DASHBOARD_TAGMANGO_CLOUD_ACCESS = 'DASHBOARD_TAGMANGO_CLOUD_ACCESS',
  DASHBOARD_SUBSCRIPTIONS_ACCESS = 'DASHBOARD_SUBSCRIPTIONS_ACCESS',

  // Custom App Permissions
  CUSTOM_APP_ACCESS = 'CUSTOM_APP_ACCESS',

  // Messages Permissions
  MESSAGES_ACCESS = 'MESSAGES_ACCESS',
  DASHBOARD_LINK_UP_ACCESS = 'DASHBOARD_LINK_UP_ACCESS',
  DASHBOARD_PATH = 'DASHBOARD_PATH',
  DASHBOARD_BANNER = 'DASHBOARD_BANNER',

  // COMMUNITY Permissions
  COMMUNITY_ACCESS = 'COMMUNITY_ACCESS',
}

export interface IUser {
  id: string | null;
  name: string | null;
  email: string | null;
  phone: number | null;
  profilePic: string | null;
  type: OnboardingStatus | null;
  userTier?: CreatorTierType;
  accountSettingTierSubscriptionExpiry?: string;
  // Razorpay autopay status of the tier subscription ("active" | "authenticated"
  // ⇒ it will auto-renew, so no pre-expiry notice).
  accountSettingTierRazorpaySubscriptionIsAutopayStatus?: string | null;
  // A tier plan already bought and queued to start later (TMG-3904): when it
  // starts, and which tier it is (named in the notice once the backend sends it).
  scheduledPlanActivateAt?: string | null;
  scheduledPlanTier?: string | null;
  userSlug: any | null;
  showInbox: boolean | null;
  approveStatus: ICreatorApproveStatus;
  shortUrl: string | null;
  disabledChat?: boolean;
  gstNumber?: string;
  currency: string;
  tmCommission?: number;
  country?: string;
  countryCode?: string;
  UAMParentCreator?: string;
  isEmailVerified?: boolean;
  requiresPostMigrationVerification?: boolean;
  isBlockedFromCommunityEngagement?: boolean;
  score?: IUserScore;
  host?: string;
  isDeactivated?: boolean;
  whitelabelPlanType?: WhitelabelPlan;
  dialCode?: string;
  userAccessPermissions?: Array<UAMPermissions>;
  badges?: string[];
  featuresConfig?: IFeaturesConfig;

  // for TQ Connect
  tqConnectScore?: number;
  tqConnectProfileCompletionResponses?: ITQProfileCompletionResponse[];
  tqConnectBadge?: string;
  tqScoreLastUpdatedAt?: string; // date the score was last updated (ISO string)
  tqReviewReceived?: number; // number of reviews received = number of service requests completed
}

export interface IDetailsType {
  name: string | null;
  id: string | null;
  profilePic: string | null;
  type: string | null;
  userSlug: any | null;
  showInbox: boolean | null;
  approveStatus: ICreatorApproveStatus;
  shortUrl: string | null;
}

export interface IOrder {
  _id: string;
  refOrders: any[];
  isZapier: boolean;
  moengageCouponUsed: boolean;
  isRefunded: boolean;
  fan: string;
  creator: string;
  mango: string;
  amount: number;
  subscriberCurrency: string;
  discount?: number;
  gst: number;
  razorpayOrderId: string;
  status: string;
  discovery: boolean;
  paymentProvider: string;
  paytmOrderId: string;
  paytmTxnToken: string;
  stripeOrderId?: any;
  coupon: string;
  juspayOrderId: string;
  successRedirectionRoute: string;
  failureRedirectionRoute: string;
  afterDiscountAmount: number;
  commissionCharged: number;
  gstForCommission: number;
  gstForCreator: number;
  afterCommissionAmount: number;
  tmCommission: number;
  convenienceFee: number;
  convenienceWithoutGst: number;
  convenienceFeeGst: number;
  afterConvenienceAmount: number;
  whitelabelHost: string;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  subscription: string;
  /** FlexiPay: matches `installmentNumber` on the corresponding installment. */
  flexiPayInstallmentNumber?: number;
  invoiceNumber?: number;
  invoice?: string;
  commissionInvoice: string;
  commissionInvoiceNumber?: number;
  convenienceFeeInvoice: string;
  convenienceFeeInvoiceNumber?: number;
  gstNumber: string;
  gstRegistrationName: string;
  gstRegistrationAddress: string;
  gstState: string;
}

export interface ICreator {
  profilePicUrl: string;
  userSlug: string;
}

export interface IFlexiPayInstallment {
  _id?: string;
  paidOn?: string;
  installmentNumber?: number;
  installmentIndex?: number;
  amount?: Record<string, number>;
  dueDate?: string;
  dueAt?: string;
  calculatedDueDate?: string;
  scheduledDueDate?: string;
  scheduledFor?: string;
}

export interface ISubscription {
  autopayEnabled?: boolean;
  _id: string;
  creator?: ICreator;
  mango: Mango;
  expiredAt?: string;
  status: string;
  subscribedAt: string;
  latestSubscriptionDate?: string;
  orders: IOrder[];
  name?: string;
  creatorId?: string;
  country: string;
  profilePicUrl?: string;
  isPublic: boolean;
  isStopTakingPayment: boolean;
  videoCallFeature: number;
  updatedAt: number;
  isMigrated: boolean;
  fanCurrency: string;
  videocall?: number;
  nextChargeAt?: string;
  razorpayChargeAtWillToken?: string;
  razorpayChargeAtWillTokenStatus?: string;
  affiliateEnabled?: boolean;
  affiliatePercentage?: number;

  isFlexiPay?: boolean;
  flexiPayInstallMentDetails?: IFlexiPayInstallment[];
  flexiPayNextDueDate?: string;
  flexiPayCurrentInstallmentNumber?: number;
  flexiPayTotalInstallments?: number;
}

export interface CreatorDetails {
  styledAbout?: string;
  showtwitter: boolean;
  showfacebook: boolean;
  showinstagram: boolean;
  showyoutube: boolean;
  showlinkedin: boolean;
  isDeactivated: boolean;
  fanCompleted: boolean;
  _id: string;
  email_verified: boolean;
  isDeleted?: boolean;
  phone: number;
  onboarding: string;
  otp?: any;
  expireIn?: any;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  email: string;
  name: string;
  profilePicUrl: string;
  profilePicUrlUncompressed?: string;
  accountName: string;
  accountNumber: string;
  bankPhone: number;
  ifsc: string;
  twitter?: string;
  facebook: string;
  instagram?: string;
  about: string;
  videoUrl: string;
  bankName: string;
  linkedin?: string;
  youtube?: string;
  birthday?: any;
  gender: string;
  razorpayLinkedAccountId: string;
  deviceId: string;
  userSlug: string;
  showSubscriberCount: boolean;
  shortUrl?: string;
  category?: string;
  subCategory?: string;
  videoThumbnail?: string;
  videoUrlUncompressed?: string;
}

export interface ActiveMango {
  additionalCoverContent?: string[];
  maxSubscriber?: number;
  remainingSlot?: number;
  includegst: boolean | undefined;
  currency?: string;
  shortUrl?: string;
  isDeleted: boolean;
  _id: string;
  isStopTakingPayment: boolean;
  isHidden: boolean;
  content: boolean;
  chat: boolean;
  videocall: number;
  creator: string;
  title: string;
  start: Date;
  end: Date;
  price: number;
  description: string;
  recurringType: string;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  razorpayPlanId: string;
  isPublic?: boolean;
  imgUrl?: string;
  videoUrl?: string;
}

export interface ICreatorDetailsResponse {
  creatordetails: CreatorDetails;
  activeMangoes: ActiveMango[];
  subscriberCount: number;
}

export interface SubscriberDetailsWithActiveMangoes {
  subscriptions: Subscription[];
  mangoes: Mango[];
}

interface Subscription {
  orders: string[];
  hideRenew: boolean;
  _id: string;
  creator: string;
  fan: string;
  mango: string;
  __v: number;
  createdAt: string;
  expiredAt: string;
  juspayOrderId: string;
  paymentProvider: string;
  paytmOrderId?: any;
  razorpayOrderId?: any;
  status: string;
  stripeOrderId?: any;
  subscribedAt: string;
  updatedAt: string;
  totalEarning: number;
  videocall: number;
  latestSubscriptionDate: string;
}
export type CreatorTierType = `${CreatorTier}`;

export enum CreatorTier {
  TIER1 = 'tier1',
  TIER2 = 'tier2',
  TIER3 = 'tier3',
  TIER4 = 'tier4',
}

export type OnboardingStatus = `${Onboarding}`;

export enum Onboarding {
  DETAILS_ADDED = 'details_added',
  STARTED = 'started',
  CREATOR_MANGO_CREATED = 'creator_mango_created',
  CREATOR_COMPLETED = 'creator_completed',
  FAN_COMPLETED = 'fan_completed',
  DUAL = 'dual',
  CREATOR_RESTRICTED = 'creator_restricted',
}

export type OnboardingStatusTypes = `${Onboarding}`[];

export enum WhitelabelPlan {
  FREEDOM = 'freedom-plan',
  ENTERPRISE = 'enterprise-plan',
}

export type UserAccountType = 'creator' | 'subscriber';

export type WhitelabelPlanType = `${WhitelabelPlan}`;

export type PlanTierType = `${PlanTier}`;

export enum PlanTier {
  TIER1 = 'tier1',
  TIER2 = 'tier2',
  TIER3 = 'tier3',
  TIER4 = 'tier4',
}

export interface IActiveUser {
  _id: string;
  name: string;
  profilePicUrl: string;
  onboarding: OnboardingStatus;
}
